/**
 * Employer Portal Layout
 *
 * Provides the layout structure for all employer portal pages including:
 * - Sidebar navigation
 * - Header with employer info
 * - Main content area
 */

import React, { useState, useEffect, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Users,
  LayoutDashboard,
  Gift,
  BarChart3,
  MessageSquare,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  Menu,
  X,
  Compass,
  Shield,
  Heart,
  Briefcase,
  Sparkles,
} from 'lucide-react';
import { getMemberData, getMemberToken, logoutMember, getCommunications } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { ErrorBoundary } from '@/components/ErrorBoundary';

interface EmployerLayoutProps {
  children: ReactNode;
}

interface Employer {
  id: number;
  company_name: string;
  employer_number: string;
  contract_status: string;
  employee_count?: number;
}

interface Employee {
  id: number;
  employee_number: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  department?: string;
}

export default function EmployerLayout({ children }: EmployerLayoutProps) {
  const { t } = useTranslation('employer');
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [employer, setEmployer] = useState<Employer | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const member = getMemberData();

  const navItems = [
    { id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, path: '/employer/dashboard' },
    { id: 'consent', label: t('nav.programConsent'), icon: Shield, path: '/employer/consent' },
    { id: 'benefits', label: t('nav.benefits'), icon: Gift, path: '/employer/benefits' },
    { id: 'navigation', label: t('nav.navigation'), icon: Compass, path: '/employer/navigation' },
    { id: 'recommendations', label: t('nav.forYou'), icon: Sparkles, path: '/employer/recommendations' },
    { id: 'leave', label: t('nav.leaveAndDisability'), icon: Briefcase, path: '/employer/leave' },
    { id: 'wellness', label: t('nav.wellness'), icon: Heart, path: '/employer/wellness' },
    { id: 'messages', label: t('nav.messages'), icon: MessageSquare, path: '/employer/messages' },
    { id: 'settings', label: t('nav.settings'), icon: Settings, path: '/employer/settings' },
  ];

  // Fetch employer and employee data
  useEffect(() => {
    const fetchData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          console.warn('No member token found - user not authenticated');
          setLoading(false);
          return;
        }

        // Fetch employee record linked to this member
        const employeeUrl = getWeaveinApiUrl(`/api/employees/by-member/${member.id}`);
        console.log('Fetching employee data from:', employeeUrl);

        const employeeResponse = await fetch(employeeUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        });

        if (employeeResponse.ok) {
          const employeeData = await employeeResponse.json();
          console.log('Employee data loaded:', employeeData?.id);
          setEmployee(employeeData);

          // Fetch employer details
          if (employeeData.employer_id) {
            const employerUrl = getWeaveinApiUrl(`/api/employers/${employeeData.employer_id}`);
            const employerResponse = await fetch(employerUrl, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                'Content-Type': 'application/json',
              },
            });

            if (employerResponse.ok) {
              const employerData = await employerResponse.json();
              console.log('Employer data loaded:', employerData?.company_name);
              setEmployer(employerData);

              // Set active_cbo_id to employer's cbo_id so service referrals and other APIs filter correctly
              if (employerData.cbo_id) {
                const currentCboId = localStorage.getItem('active_cbo_id');
                if (currentCboId !== String(employerData.cbo_id)) {
                  console.log('🏢 Setting active_cbo_id to employer CBO:', employerData.cbo_id, '(was:', currentCboId, ')');
                  localStorage.setItem('active_cbo_id', String(employerData.cbo_id));
                }
              }

              // Set active_employer_id so Service Referrals page can filter by employer
              if (employerData.id) {
                const currentEmployerId = localStorage.getItem('active_employer_id');
                if (currentEmployerId !== String(employerData.id)) {
                  console.log('🏢 Setting active_employer_id:', employerData.id, '(was:', currentEmployerId, ')');
                  localStorage.setItem('active_employer_id', String(employerData.id));
                }
              }
            } else {
              console.warn('Failed to fetch employer:', employerResponse.status);
            }
          }
        } else {
          console.warn('Failed to fetch employee record:', employeeResponse.status);
          // This is expected if user has no employee record - not an error
        }
      } catch (error) {
        console.error('Error fetching employer data:', error);
        // Don't crash - just log and continue with empty state
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [member?.id]);

  // Fetch unread message count — delayed to avoid racing with page-level data loads
  useEffect(() => {
    let cancelled = false;

    const fetchUnreadCount = async () => {
      if (!member?.id || cancelled) return;

      try {
        const communications = await getCommunications(true); // unread only
        if (!cancelled) {
          setUnreadCount(communications?.length || 0);
        }
      } catch (err) {
        console.error('Failed to fetch unread count:', err);
      }
    };

    const initialTimeout = setTimeout(fetchUnreadCount, 2000);

    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [member?.id]);

  const handleLogout = () => {
    logoutMember();
    navigate('/member/login');
  };

  const currentPath = location.pathname;
  const activeItem = navItems.find(item => currentPath.startsWith(item.path))?.id || 'dashboard';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('layout.loading')}</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('layout.authRequired')}</h2>
          <p className="text-gray-600 mb-6">{t('layout.signInMsg')}</p>
          <Link
            to="/member/login"
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {t('layout.signIn')}
          </Link>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('layout.noEmployeeRecord')}</h2>
          <p className="text-gray-600 mb-6">
            {t('layout.noEmployeeMsg')}
          </p>
          <Link
            to="/member/dashboard"
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {t('layout.goToMemberPortal')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          bg-white border-r border-gray-200 transition-all duration-300
          flex flex-col
        `}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <div className="flex items-center">
              <img src="/weavin-logo.png" alt="WeaveIn" className="h-10 w-auto" />
            </div>
          )}
          {sidebarCollapsed && <img src="/weavin-logo.png" alt="WeaveIn" className="h-8 w-auto mx-auto" />}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex p-1 rounded hover:bg-gray-100"
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-1 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Company Info */}
        {employer && !sidebarCollapsed && (
          <div className="px-4 py-3 border-b border-gray-200 bg-indigo-50">
            <p className="text-sm font-medium text-indigo-900 truncate">{employer.company_name}</p>
            <p className="text-xs text-indigo-600">{employee.department || 'Employee'}</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem === item.id;
              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center px-3 py-2.5 rounded-lg transition-colors
                      ${isActive
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-gray-500'}`} />
                    {!sidebarCollapsed && <span className="ml-3 font-medium">{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Section */}
        <div className="border-t border-gray-200 p-4">
          {!sidebarCollapsed ? (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 font-semibold">
                  {employee.first_name[0]}{employee.last_name[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {employee.first_name} {employee.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{employee.job_title || 'Employee'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded hover:bg-gray-100"
                title="Sign out"
              >
                <LogOut className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full p-2 rounded hover:bg-gray-100 flex justify-center"
              title="Sign out"
            >
              <LogOut className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('layout.searchPlaceholder')}
                className="pl-10 pr-4 py-2 w-64 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Link
              to="/employer/messages"
              className="relative p-2 rounded hover:bg-gray-100"
              title={unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : t('nav.messages')}
            >
              <Bell className="w-5 h-5 text-gray-500" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            <Link
              to="/member/dashboard"
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {t('layout.memberPortal')}
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <ErrorBoundary sectionName="page content">
            {children}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
